import { Injectable, signal, computed } from '@angular/core';
import {
  ChatMessage,
  DifyStreamCallbacks,
  DifyStreamEvent,
  DifyParameters,
  DifyConversationsResponse,
  DifyMessagesResponse,
  ConnectionStatus,
} from './types';

/**
 * Dify 服务统一基类。
 *
 * 负责所有 Dify 相关的通用逻辑：
 * - 连接验证（/parameters）
 * - 会话历史加载（/conversations + /messages）
 * - 流式对话（/chat-messages）
 * - 消息列表 / 会话状态 / 用户上下文
 *
 * 子类只需提供：
 * - `apiUrl`：Dify 应用 API 地址
 * - `apiKey`：Dify 应用密钥
 * - `buildInputs()`：定制聊天入参（通用助手 vs 语音报报告各不同）
 */
@Injectable({ providedIn: 'root' })
export abstract class DifyBaseService {
  /** 子类提供：Dify 应用 API 地址（含 /v1） */
  protected abstract readonly apiUrl: string;
  /** 子类提供：Dify 应用 API Key */
  protected abstract readonly apiKey: string;

  private abortController: AbortController | null = null;

  /** 当前会话消息列表 */
  messages = signal<ChatMessage[]>([]);
  /** 是否正在流式输出 */
  isStreaming = signal(false);
  /** 当前 Dify 会话 id */
  conversationId: string | null = null;

  /**
   * 是否在 initialize 时自动加载该用户最近的会话历史。
   * - 通用助手：true（每次打开恢复最近对话）
   * - 语音报报告：false（Dify 按 user 混存 conversation，无法区分通用/报告，
   *   其会话生命周期由前端状态驱动，见 DifyVoiceReportService）
   */
  protected loadHistoryOnInit = true;

  /**
   * 该服务是否启用。
   * - 通用助手（TVMS快捷查询）：false = 暂时禁用（家用网络访问不到远程 Dify）
   * - 语音报报告：true（本地 Dify，不受影响）
   * 禁用时 initialize() 直接置为 error 并抛错，不发起任何请求。
   */
  protected enabled = true;

  /** 连接状态 */
  connectionStatus = signal<ConnectionStatus>('idle');
  /** 应用参数（opening_statement 等） */
  parameters = signal<DifyParameters | null>(null);

  /** 用户上下文 */
  userEmail = signal<string>('');
  userRoles = signal<string>('');
  userDisplayName = signal<string>('');
  userToken = signal<string>('');

  setUser(email: string, roles: string, displayName: string, token: string): void {
    this.userEmail.set(email);
    this.userRoles.set(roles);
    this.userDisplayName.set(displayName);
    this.userToken.set(token);
  }

  processedOpeningStatement = computed(() => {
    const statement = this.parameters()?.opening_statement;
    if (!statement) return '';
    return statement
      .replace(/\{\{user_name\}\}/g, this.userDisplayName() || 'User')
      .replace(/\{\{user_role\}\}/g, this.userRoles() || 'Guest');
  });

  /**
   * 子类定制：构建聊天请求入参。
   * 默认注入用户上下文，子类可在此基础上追加字段（如 report_type、prefilled_fields）。
   */
  protected buildInputs(): Record<string, unknown> {
    return {
      user_email: this.userEmail() || '',
      user_role: this.userRoles() || '',
      user_name: this.userDisplayName() || '',
      // MCP 签名：使用 TVMS 快捷查询前由 signin 换取的 token，Dify 据此校验身份
      X_MCP_Signature: this.userToken() || '',
    };
  }

  async initialize(): Promise<void> {
    if (!this.enabled) {
      this.connectionStatus.set('error');
      throw new Error('服务暂不可用');
    }
    if (this.connectionStatus() === 'connected') return;

    this.connectionStatus.set('connecting');
    try {
      const params = await this.verifyConnection();
      this.parameters.set(params);
      this.connectionStatus.set('connected');
      if (this.loadHistoryOnInit) {
        await this.loadHistory();
      }
    } catch (err: any) {
      this.connectionStatus.set('error');
      throw err;
    }
  }

  private async verifyConnection(): Promise<DifyParameters> {
    const response = await fetch(`${this.apiUrl}/parameters`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${this.apiKey}` },
    });

    if (!response.ok) {
      throw new Error(`参数接口连接失败: HTTP ${response.status}`);
    }

    return response.json() as Promise<DifyParameters>;
  }

  private async loadHistory(): Promise<void> {
    const user = this.userEmail();
    if (!user) return;

    try {
      const convUrl = `${this.apiUrl}/conversations?user=${encodeURIComponent(user)}&limit=1`;
      const convRes = await fetch(convUrl, {
        headers: { Authorization: `Bearer ${this.apiKey}` },
      });
      if (!convRes.ok) return;

      const convData: DifyConversationsResponse = await convRes.json();
      if (!convData.data?.length) return;

      const lastConv = convData.data[0];
      this.conversationId = lastConv.id;

      const msgUrl = `${this.apiUrl}/messages?conversation_id=${lastConv.id}&user=${encodeURIComponent(user)}`;
      const msgRes = await fetch(msgUrl, {
        headers: { Authorization: `Bearer ${this.apiKey}` },
      });
      if (!msgRes.ok) return;

      const msgData: DifyMessagesResponse = await msgRes.json();
      const history: ChatMessage[] = [];

      for (const m of msgData.data || []) {
        history.push({
          id: `${m.id}-q`,
          role: 'user',
          content: m.query,
          timestamp: m.created_at * 1000,
          status: 'done',
        });
        history.push({
          id: `${m.id}-a`,
          role: 'assistant',
          content: m.answer,
          timestamp: m.created_at * 1000,
          status: 'done',
        });
      }

      this.messages.set(history);
    } catch {
      // 历史记录加载失败不影响主流程
    }
  }

  async sendMessage(query: string, callbacks: DifyStreamCallbacks): Promise<void> {
    if (!query.trim()) return;

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: query,
      timestamp: Date.now(),
      status: 'done',
    };
    this.messages.update(m => [...m, userMessage]);

    const assistantMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'assistant',
      content: '',
      timestamp: Date.now(),
      status: 'streaming',
    };
    this.messages.update(m => [...m, assistantMessage]);
    this.isStreaming.set(true);

    this.abortController = new AbortController();

    try {
      const response = await fetch(`${this.apiUrl}/chat-messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.apiKey}`,
        },
        body: JSON.stringify({
          inputs: this.buildInputs(),
          query,
          response_mode: 'streaming',
          user: this.userEmail() || 'tvms-user',
          conversation_id: this.conversationId,
        }),
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const jsonStr = line.slice(6).trim();
          if (!jsonStr) continue;
          if (jsonStr === '[DONE]') continue;

          try {
            const event: DifyStreamEvent = JSON.parse(jsonStr);
            this.handleEvent(event, callbacks);
          } catch {
            /* skip malformed lines */
          }
        }
      }
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      this.messages.update(m => {
        const last = m[m.length - 1];
        if (last?.role === 'assistant') {
          const updated = { ...last, status: 'error' as const };
          return [...m.slice(0, -1), updated];
        }
        return m;
      });
      callbacks.onError(err.message || 'Request failed');
    } finally {
      this.isStreaming.set(false);
      this.abortController = null;
    }
  }

  private handleEvent(event: DifyStreamEvent, callbacks: DifyStreamCallbacks): void {
    switch (event.event) {
      case 'message':
      case 'message_replace':
        if (event.answer) {
          this.messages.update(m => {
            const last = m[m.length - 1];
            if (last?.role === 'assistant') {
              const updated = { ...last, content: last.content + event.answer! };
              return [...m.slice(0, -1), updated];
            }
            return m;
          });
          callbacks.onChunk(event.answer);
        }
        break;

      case 'message_end':
        this.conversationId = event.conversation_id || this.conversationId;
        this.messages.update(m => {
          const last = m[m.length - 1];
          if (last?.role === 'assistant') {
            const updated = { ...last, status: 'done' as const };
            return [...m.slice(0, -1), updated];
          }
          return m;
        });
        callbacks.onDone(event.conversation_id || '', event.id || '');
        break;

      case 'error':
        this.messages.update(m => {
          const last = m[m.length - 1];
          if (last?.role === 'assistant') {
            const updated = { ...last, status: 'error' as const };
            return [...m.slice(0, -1), updated];
          }
          return m;
        });
        callbacks.onError(event.error || 'Unknown error');
        break;
    }
  }

  cancel(): void {
    this.abortController?.abort();
    this.isStreaming.set(false);
    this.messages.update(m => {
      const last = m[m.length - 1];
      if (last?.role === 'assistant' && last.status === 'streaming') {
        const updated = {
          ...last,
          status: 'error' as const,
          content: last.content + '\n\n*已取消*',
        };
        return [...m.slice(0, -1), updated];
      }
      return m;
    });
  }

  clearMessages(): void {
    this.messages.set([]);
    this.conversationId = null;
  }

  /**
   * 开启一段全新的 Dify 会话。
   * 仅将 conversationId 置空，不清空消息列表（保留当前对话展示）。
   * 下次发送消息时 Dify 会创建新会话并返回新 conversation_id，
   * 用于"新建报告"时确保每份报告使用独立的会话，互不串数据。
   */
  startNewConversation(): void {
    this.conversationId = null;
  }

  retryLast(callbacks: DifyStreamCallbacks): void {
    const msgs = this.messages();
    const lastUserMsg = [...msgs].reverse().find(m => m.role === 'user');
    if (!lastUserMsg) return;

    this.messages.update(m => m.slice(0, -2));
    this.sendMessage(lastUserMsg.content, callbacks);
  }
}
